export type NotificationKind = 'success' | 'error' | 'warning' | 'info' | 'loading';
export interface Notice { id: string; kind: NotificationKind; title: string; description?: string; duration?: number }
export interface NoticeInput { id?: string; description?: string; duration?: number }
export interface Confirmation { title: string; description: string; confirmLabel?: string }
interface PendingConfirmation extends Confirmation { id: string; returnFocus: HTMLElement | null; resolve: (accepted: boolean) => void }
interface FeedbackState { notices: Notice[]; confirmation: PendingConfirmation | null }
let state: FeedbackState = { notices: [], confirmation: null };
let sequence = 0;
let epoch = 0;
let hosts = 0;
const listeners = new Set<() => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const emit = () => listeners.forEach(listener => listener());
function dismiss(id: string) {
  clearTimeout(timers.get(id)); timers.delete(id);
  state = { ...state, notices: state.notices.filter(notice => notice.id !== id) }; emit();
}
function publish(kind: NotificationKind, title: string, options: NoticeInput = {}) {
  const id = options.id ?? `feedback-${++sequence}`;
  clearTimeout(timers.get(id)); timers.delete(id);
  const duration = options.duration ?? (kind === 'success' ? 6500 : kind === 'info' ? 9000 : 0);
  const notice: Notice = { id, kind, title, ...options, duration };
  const notices = [...state.notices.filter(item => item.id !== id && !(kind === 'success' && item.kind === kind && item.title === title && item.description === options.description)), notice];
  // Bound transient history, retaining unresolved errors and in-flight requests.
  while (notices.length > 5) {
    const removable = notices.findIndex(item => item.kind === 'success' || item.kind === 'info');
    if (removable < 0) break;
    notices.splice(removable, 1);
  }
  state = { ...state, notices }; emit();
  if (duration > 0) timers.set(id, setTimeout(() => dismiss(id), duration));
  return id;
}
export const notify = {
  success: (title: string, options?: NoticeInput) => publish('success', title, options),
  error: (title: string, options?: NoticeInput) => publish('error', title, options),
  warning: (title: string, options?: NoticeInput) => publish('warning', title, options),
  info: (title: string, options?: NoticeInput) => publish('info', title, options),
  loading: (title: string, options?: NoticeInput) => publish('loading', title, options),
  dismiss,
  pause: (id: string) => { clearTimeout(timers.get(id)); timers.delete(id); },
  resume: (id: string) => {
    const notice = state.notices.find(item => item.id === id);
    if (notice?.duration && notice.duration > 0) {
      clearTimeout(timers.get(id)); timers.set(id, setTimeout(() => dismiss(id), notice.duration));
    }
  },
  clear: () => {
    epoch++;
    timers.forEach(clearTimeout); timers.clear();
    const pending = state.confirmation;
    state = { notices: [], confirmation: null }; emit(); pending?.resolve(false);
  },
};
export const feedbackStore = {
  subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
  getSnapshot: () => state,
  getEpoch: () => epoch,
  attach: () => { hosts++; return () => { hosts--; if (!hosts) feedbackStore.answer(false); }; },
  answer: (accepted: boolean) => {
    const pending = state.confirmation;
    state = { ...state, confirmation: null }; emit(); pending?.resolve(accepted);
  },
};
export function confirmAction(confirmation: Confirmation, returnFocus: HTMLElement | null = document.activeElement instanceof HTMLElement ? document.activeElement : null): Promise<boolean> {
  // Fail closed without a mounted host; never silently approve concurrent dialogs.
  if (!hosts || state.confirmation) return Promise.resolve(false);
  return new Promise(resolve => {
    state = { ...state, confirmation: { ...confirmation, id: `confirmation-${++sequence}`, returnFocus, resolve } }; emit();
  });
}
export class UnconfirmedActionError extends Error { constructor() { super('Status not confirmed. Refresh the authoritative record before retrying.'); } }
export function isUnconfirmed(error: unknown) {
  const record = error && typeof error === 'object' ? error as { message?: unknown; name?: unknown; code?: unknown } : {};
  const message = String(record.message ?? '');
  if (record.code === 'invalid_credentials') return 'The email or password could not be verified. Check both and try again.';
  if (record.code === 'email_not_confirmed') return 'Confirm your email using the message sent to your inbox before signing in.';
  if (record.code === 'over_email_send_rate_limit' || record.code === 'over_request_rate_limit') return 'Too many requests were made. Wait a few minutes before trying again.';
  return error instanceof UnconfirmedActionError || /timeout|timed out|fetch|network|connection|not confirmed|readback|could not be reloaded|could not be refreshed|status refresh|not confirm/i.test(message) || record.name === 'AbortError';
}
export function safeActionError(error: unknown) {
  const record = error && typeof error === 'object' ? error as { code?: unknown; message?: unknown } : {};
  // Preserve actionable, authored business outcomes without echoing arbitrary
  // database text, identifiers, URLs, SQL, document names or payment references.
  const message = String(record.message ?? '');
  const knownMessages = [
    'Cancellation was not confirmed by the authoritative order record. Refresh before trying again.',
    'The requested access state was not confirmed by the authoritative rider record. Refresh before retrying.',
  ];
  if (knownMessages.includes(message)) return message;
  if (record.code === 'PGRST301' || record.code === 'PGRST303' || /^JWT (?:is )?expired$/i.test(message)) return 'Your session could not be verified. Sign in again, then return to this form and retry. No successful submission is confirmed.';
  if (message === 'Location is outside the service area') return 'These pins are outside the configured service area. Choose permitted locations or contact support before submitting again.';
  if (/financial activity|purchase receipt activity/.test(message)) return 'This order has financial or purchase activity and must be reconciled before any closure action.';
  if (/rider assignment history/.test(message)) return 'This order has rider assignment history and must use the separate resolution workflow.';
  if (/active dispatch state/.test(message)) return 'A dispatch attempt or rider offer is active. Wait for it to resolve, then refresh before trying again.';
  if (record.message === 'Popup blocked') return 'Your browser blocked the document window. Allow popups for this site and open the document again.';
  if (isUnconfirmed(error)) return 'The result is not confirmed. Refresh the current record before retrying; the request may have reached the server.';
  if (record.code === '42501' || /permission|not authorized|admin required|access denied/i.test(String(record.message ?? ''))) return 'Your account is not authorized for this action. Refresh your account access or contact an administrator.';
  if (record.code === 'PGRST202' || record.code === '42883') return 'This feature is unavailable on the server. Refresh or contact support before retrying.';
  if (record.code === '23505') return 'A matching record already exists. Refresh and check it before submitting again.';
  return 'The request could not be completed. Review the form and current record, then try again. If it continues, contact support.';
}
